import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import api from '../../../const/axios'
import RatingColumnHeader, { RATING_COLUMN_HINTS } from './RatingColumnHeader'

type Row = {
  tournamentId: number
  entityId: number
  entityName?: string
  expectedPerf: number
  actualPerf: number
  kValue: number
  delta: number
  newRating: number
  publishedAt: string
  calculationVersion: string
}

type Breakdown = {
  tournamentId: number
  tournamentName?: string
  teamDeltas: Row[]
  playerDeltas: Row[]
}

const TournamentRatingBreakdown: React.FC = () => {
  const { id } = useParams()
  const [data, setData] = useState<Breakdown | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!id) return

    const load = async () => {
      setLoading(true)
      setError('')

      try {
        const res = await api.get(`/tournaments/${id}/rating-breakdown`)
        setData(res.data)
      } catch (err: any) {
        setError(err?.response?.data || 'Turnir breakdown yüklənmədi.')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [id])

  const renderTable = (rows: Row[], title: string, entity: 'teams' | 'players') => (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-slate-100 dark:border-gray-800 shadow-sm overflow-x-auto">
      <div className="px-4 py-3 border-b border-slate-100 dark:border-gray-800 font-semibold text-slate-800 dark:text-gray-100">{title}</div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-gray-800/60 border-y border-slate-100 dark:border-gray-700 text-gray-500 dark:text-gray-400">
          <tr>
            <RatingColumnHeader label="#" align="left" />
            <RatingColumnHeader label={entity === 'teams' ? 'Komanda' : 'Oyunçu'} align="left" />
            <RatingColumnHeader label="Gözlənilən" hint={RATING_COLUMN_HINTS.expected} />
            <RatingColumnHeader label="Faktiki" hint={RATING_COLUMN_HINTS.actual} />
            <RatingColumnHeader label="K" hint={RATING_COLUMN_HINTS.k} />
            <RatingColumnHeader label="Dəyişmə" hint={RATING_COLUMN_HINTS.delta} />
            <RatingColumnHeader label="Yeni Reytinq" />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={7} className="text-center px-3 py-8 text-slate-400 dark:text-gray-500">Məlumat yoxdur</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 dark:border-gray-800 hover:bg-slate-50 dark:hover:bg-gray-800/50 transition-colors">
              <td className="px-3 py-2 text-slate-500 dark:text-gray-400">{i + 1}</td>
              <td className="px-3 py-2">
                <Link
                  to={`/ratings/${entity}/${r.entityId}`}
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold"
                >
                  {r.entityName || `#${r.entityId}`}
                </Link>
              </td>
              <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{Number(r.expectedPerf || 0).toFixed(3)}</td>
              <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{Number(r.actualPerf || 0).toFixed(3)}</td>
              <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{r.kValue}</td>
              <td className={`px-3 py-2 text-right font-semibold ${(r.delta || 0) >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}`}>
                {Number(r.delta || 0).toFixed(3)}
              </td>
              <td className="px-3 py-2 text-right font-bold text-gray-800 dark:text-gray-100">{Number(r.newRating || 0).toFixed(3)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-950 pt-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-left min-w-0">
            {/* <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400">
              Turnir Reytinq Breakdown
            </p> */}
            <h1 className="text-2xl font-black text-left tracking-normal text-slate-900 dark:text-gray-100 break-words">
              {data?.tournamentName || `#${id}`}
            </h1>
          </div>
          <Link to="/ratings" className="text-sm text-blue-700 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300">Reytinq səhifəsinə qayıt</Link>
        </div>

        {loading ? <p className="text-slate-400 dark:text-gray-500">Yüklənir...</p> : null}
        {error ? <p className="text-red-600 dark:text-red-400 text-sm">{error}</p> : null}

        {data ? (
          <>
            {renderTable(data.teamDeltas || [], 'Komanda Delta Cədvəli', 'teams')}
            {renderTable(data.playerDeltas || [], 'Oyunçu Delta Cədvəli', 'players')}
          </>
        ) : null}
      </div>
    </div>
  )
}

export default TournamentRatingBreakdown
